<template>
    <div>
        <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>

        <!-- Payment methods list-->
        <template v-if="paymentMethods && !showCreditCardCollectionForm && !showSecurityCodeCollectionForm">
            <h6 class="content">Seleccioná la forma de pago que quieras usar</h6>

            <!-- Saved payment methods -->
            <template v-if="paymentMethods.saved">
                <ul v-if="paymentMethods.saved" class="biglist">
                    <li v-for="method in paymentMethods.saved"
                        :key="method.last"
                        @click="onPaymentMethodSelected(method)"
                    >
                        <div class="level is-mobile">
                            <div class="level-left">
                                <div class="level-item">
                                    <span class="icon is-large"><i v-bind:class="[ getIcon(method) ]"></i></span>
                                </div>

                                <div class="level-item">
                                    <span class="address-title">Tu {{ getCardName(method.name) }} terminada en {{ method.last }}</span>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </template>

            <!-- Other payment methods -->
            <ul class="biglist">
                <li v-for="method in paymentMethods.methods"
                    :key="method.id"
                    @click="onPaymentMethodSelected(method)"
                >
                    <div class="level is-mobile">
                        <div class="level-left">
                            <div class="level-item">
                                <span class="icon is-large"><i v-bind:class="[ getIcon(method) ]"></i></span>
                            </div>

                            <div class="level-item">
                                <span class="address-title">{{ method.name }}</span>
                            </div>
                        </div>
                    </div>
                </li>

                <li @click="onPaymentMethodSelected(null)">
                    <div class="level is-mobile">
                        <div class="level-left">
                            <div class="level-item">
                                <span class="icon is-large"><i class="fas fa-2x fa-money-bill-wave"></i></span>
                            </div>

                            <div class="level-item">
                                <span class="address-title">Pago en la entrega (Crédito, Débito, Efectivo)</span>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </template>

        <!--Security Code Collection Dialog -->
        <template v-if="showSecurityCodeCollectionForm">
            <div class="cc_info">
                <div class="columns">
                    <div class="column">
                        <b-field label="Codigo de Seguridad">
                            <input ref="cvc2"
                                   v-card-focus
                                   name="cvc"
                                   v-model="cardDetail.cvc"
                                   class="input"
                                   placeholder="123" />
                        </b-field>
                    </div>

                    <div class="column is-three-fifths is-hidden-touch">
                        <card
                                style="margin-top: 30px;"
                                v-model="cardDetail">
                        </card>
                    </div>
                </div>

                <nav class="buttons is-right">
                    <a @click="onChooseAnotherPaymentMethodSelected" style="margin-right: 20px;">Elegir otro método de pago</a>
                    <b-button type="is-primary" @click="onSecurityCodeConfirmationSelected">Continuar</b-button>
                </nav>
            </div>
        </template>

        <!--Credit Card Collection Dialog-->
        <template v-if="showCreditCardCollectionForm">
            <div class="cc_info">
                <div class="columns">
                    <div class="column">
                        <b-field label="Nombre en la tarjeta">
                            <b-input v-card-focus
                                     name="name"
                                     v-model="cardDetail.name"
                                     v-validate="'required'"
                                     :disabled="selectedPaymentMethodData.id !== 5">
                            </b-input>
                        </b-field>

                        <b-field label="Numero" :message="errors.first('ccnumber')">
                            <div class="control has-icons-left">
                                <cleave v-card-focus
                                        name="number"
                                        class="input"
                                        v-validate="'required|credit_card'"
                                        v-model="cardDetail.number"
                                        placeholder=""
                                        :options="{ creditCard: true }"
                                        :disabled="selectedPaymentMethodData.id !== 5">
                                </cleave>

                                <span class="icon is-medium is-left"><i v-bind:class="[ccIcon, 'fa-lg']"></i></span>
                            </div>
                        </b-field>

                        <b-field grouped>
                            <b-field expanded label="Vencimiento">
                                <cleave v-card-focus
                                        name="expiry"
                                        v-model="cardDetail.expiry"
                                        class="input"
                                        placeholder="MM/AA"
                                        :options="{ date: true, datePattern: ['m', 'y'] }">
                                </cleave>
                            </b-field>

                            <b-field expanded label="CVV" :message="errors.first('cvc')">
                                <input v-card-focus
                                       name="cvc"
                                       v-model="cardDetail.cvc"
                                       class="input"
                                       placeholder="123"/>
                            </b-field>
                        </b-field>

                        <b-field grouped>
                            <b-field expanded label="Tipo">
                                <b-select v-model="cardDetail.docType">
                                    <option v-for="type in documentTypes"
                                            :value="type"
                                            :key="type">
                                        {{ type }}
                                    </option>
                                </b-select>
                            </b-field>

                            <b-field expanded label="Numero">
                                <input name="docNumber"
                                       v-model="cardDetail.docNumber"
                                       class="input"
                                       placeholder="XXXXXXXX"/>
                            </b-field>
                        </b-field>
                    </div>

                    <div class="column is-half is-hidden-touch">
                        <card style="margin-top: 30px;" v-model="cardDetail"></card>
                    </div>
                </div>

                <nav class="buttons is-right">
                    <a @click="onChooseAnotherPaymentMethodSelected" style="margin-right: 20px;">Elegir otro método de pago</a>
                    <b-button type="is-primary" @click="onCreditCardConfirmationSelected">Continuar</b-button>
                </nav>
            </div>
        </template>
    </div>
</template>

<script>
    /* global Mercadopago */
    
    import Cleave from 'vue-cleave';
    import Card from '../../components/credit-card/CreditCard';

    export default {
        components: {
            Cleave,
            Card
        },
        props: ['active', 'value'],

        data() {
            return {
                isLoading: false,

                showCreditCardCollectionForm: false,
                showSecurityCodeCollectionForm: false,

                documentTypes: ['DNI', 'CI', 'LC', 'LE', 'Otro'],

                ccIcon: 'far fa-credit-card',
                ccIcons: {
                    unknown: 'far fa-credit-card',
                    visa: 'fab fa-cc-visa',
                    amex: 'fab fa-cc-amex',
                    maestro: 'fab fa-cc-mastercard',
                    mastercard: 'fab fa-cc-mastercard'
                },

                paymentMethods: null,

                selectedPaymentMethodData: {},

                cardDetail: {
                    type: '',
                    number: '',
                    name: '',
                    expiry: '',
                    cvc: '',
                    docType: 'DNI',
                    docNumber: ''
                },
            }
        },

        watch: {
            ccType(val) {
                // adjust the icon
                if(this.ccIcons[val]) {
                    this.ccIcon = this.ccIcons[val];
                } else {
                    this.ccIcon = 'far fa-credit-card';
                }
            },

            ccNumber(val) {
                // if we have 6 or more digits we can guess the CC type
                let strippedVal = val.replace(/\s/g, '');
                if(strippedVal.length >= 6) {
                    Mercadopago.getPaymentMethod({
                        bin: strippedVal.substr(0, 6)
                    }, (status, response) => {
                        this.cardDetail.type = response[0].id;
                    });
                }
            }
        },

        computed: {
            ccType() {
                return this.cardDetail.type;
            },
            ccName() {
                return this.cardDetail.name;
            },
            ccNumber() {
                return this.cardDetail.number;
            },
            ccExpiry() {
                return this.cardDetail.expiry;
            },
            ccCVC() {
                return this.cardDetail.cvc;
            }
        },

        async mounted() {
            Mercadopago.setPublishableKey(process.env.VUE_APP_MERCADOPAGO_PUBLIC_KEY);

            this.isLoading = true;

            try {
                let customer = this.$store.getters['customer/get']();
                let response = await this.$api.getCustomerPaymentMethods(customer.id);
                this.paymentMethods = response;
            }
            finally {
                this.isLoading = false;
            }
        },

        methods: {
            getIcon(method) {
                switch(method.id) {
                    case 1:
                        return 'fas fa-2x fa-money-bill-wave';
                    case 2:
                        return 'fas fa-2x fa-money-check-alt';
                    case 3:
                        return 'fas fa-2x fa-address-card';
                    case 4:
                        return 'fas fa-2x fa-fax';
                    case 5:
                        return 'fas fa-2x fa-credit-card';
                }
            },

            getCardName(type) {
                switch(type) {
                    case 'visa':
                        return 'Visa';
                    case 'amex':
                        return 'American Express';
                    case 'master':
                        return 'MasterCard';
                    default:
                        return type.charAt(0).toUpperCase() + type.slice(1)
                }
            },

            onPaymentMethodSelected(value) {
                this.selectedPaymentMethodData = value;

                if(!value) {
                    this.$emit('input', {
                        method: -1
                    });
                    return;
                }

                this.cardDetail = {
                    type: '',
                    number: '',
                    name: '',
                    expiry: '',
                    cvc: '',
                    docType: 'DNI',
                    docNumber: ''
                };

                if(value.last) {
                    this.showSecurityCodeCollectionForm = true;

                    this.cardDetail.type = value.name;
                    this.cardDetail.name = value.cardholder;

                    this.$nextTick(() => {
                        this.$refs.cvc2.focus();
                    });
                } else if(value.id === 5) {
                    this.showCreditCardCollectionForm = true;
                } else {
                    this.$emit('input', {
                        method: value.id,
                        name: value.name
                    });
                }
            },

            onChooseAnotherPaymentMethodSelected() {
                this.showCreditCardCollectionForm = false;
                this.showSecurityCodeCollectionForm = false;
            },

            onSecurityCodeConfirmationSelected() {
                if(this.cardDetail.cvc.length < 3) {
                    return;
                }

                this.isLoading = true;

                // Grab the data to send to MP
                let mpFormData = {
                    cardId: this.selectedPaymentMethodData.token,
                    securityCode: this.cardDetail.cvc
                };

                Mercadopago.clearSession();
                Mercadopago.createToken(mpFormData, (status, response) => {
                    this.showSecurityCodeCollectionForm = false;
                    this.isLoading = false;

                    if(status !== 200 && status !== 201) {
                        console.error('Error getting token for saved card');
                    } else {
                        this.$emit('input', {
                            method: 5,
                            cc_type: this.selectedPaymentMethodData.name,
                            cc_last: this.selectedPaymentMethodData.last,
                            cc_token: response.id
                        });
                    }
                });
            },

            onCreditCardConfirmationSelected(value) {
                this.isLoading = true;

                // Grab the data to send to MP
                let exp = this.cardDetail.expiry.replace(/\s/g, '').split('/');
                let mpFormData = {
                    cardNumber: this.cardDetail.number.replace(/\s/g, ''),
                    securityCode: this.cardDetail.cvc,
                    cardExpirationMonth: exp[0],
                    cardExpirationYear: exp[1],
                    cardholderName: this.cardDetail.name,
                    docType: this.cardDetail.docType,
                    docNumber: this.cardDetail.docNumber
                };

                Mercadopago.clearSession();
                Mercadopago.createToken(mpFormData, (status, response) => {
                    this.isLoading = false;

                    if(status !== 200 && status !== 201) {
                        this.$buefy.dialog.alert({
                            title: 'Error',
                            message: 'Tu método de pago fue rechazado, por favor revisá los datos o intentá con otro medio de pago',
                            type: 'is-danger',
                            hasIcon: true,
                            icon: 'times-circle',
                            iconPack: 'fa'
                        });
                    } else {
                        this.showCreditCardCollectionForm = false;

                        this.$emit('input', {
                            method: 5,
                            cc_type: this.cardDetail.type,
                            cc_last: response.last_four_digits,
                            cc_token: response.id,
                            cc_doc_type: this.cardDetail.docType,
                            cc_doc_number: this.cardDetail.docNumber
                        });
                    }
                });
            }
        }
    }
</script>

<style scoped>
    .cc_info {
        background-color: #f9f8f4;
        border-radius: 5px;
        padding: 20px;
    }
</style>